import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import { PRESETS, defaultPeriodsFor, fetchGradingPeriods } from "../../utils/gradingPeriods";
import s from "./SettingsPage.module.css";
import g from "./GradingPeriodsCard.module.css";

/* Settings → Academic Settings → Grading Periods.
 * Each Level (TEACHING department) gets an ordered list of periods — the
 * columns teachers encode and students see. A Level never saved here uses
 * its default: whole-year 1st–4th Quarter (Senior High: 2 per semester). */

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
function authHeaders() {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
  };
}

const levelName = (d) => (d?.name || "").replace(/\s*department\s*$/i, "");
const SEM_OPTIONS = [
  { value: "1",  label: "1st semester" },
  { value: "2",  label: "2nd semester" },
  { value: "",   label: "Every semester" },
];

/** Stable code for a new custom period: P1, P2, … not already used. */
function nextCode(rows) {
  const used = new Set(rows.map(r => r.code));
  for (let i = 1; i < 100; i++) if (!used.has(`P${i}`)) return `P${i}`;
  return `P${Date.now() % 100000}`;
}

export default function GradingPeriodsCard() {
  const [levels,  setLevels]  = useState([]);
  const [saved,   setSaved]   = useState([]);  // all rows from the server
  const [levelId, setLevelId] = useState("");
  const [rows,    setRows]    = useState([]);  // editable list for levelId
  const [busy,    setBusy]    = useState(false);
  const [msg,     setMsg]     = useState("");
  const [err,     setErr]     = useState("");

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/departments?staffType=TEACHING`).then(r => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => (r.ok ? r.json() : [])).catch(() => []),
      fetchGradingPeriods(),
    ]).then(([depts, gradeLevels, periodRows]) => {
      const all = Array.isArray(depts) ? depts : [];
      const gls = Array.isArray(gradeLevels) ? gradeLevels : [];
      // Grading follows grade level → Level, so only Levels that own grade
      // levels matter. This also drops legacy duplicate department rows
      // (e.g. "Elementary" next to "Elementary Department") that no grade
      // level points at. Ordered by school progression via the lowest
      // grade ordering, same as the Class Offerings Level tabs.
      const minOrder = new Map();
      for (const gl of gls) {
        if (gl.departmentId == null) continue;
        const o = gl.ordering ?? 0;
        if (!minOrder.has(gl.departmentId) || o < minOrder.get(gl.departmentId)) minOrder.set(gl.departmentId, o);
      }
      const owning = all.filter(d => minOrder.has(d.id));
      const list = (owning.length ? owning : all).slice().sort((a, b) =>
        (minOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (minOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER)
        || levelName(a).localeCompare(levelName(b)));
      setLevels(list);
      setSaved(periodRows);
      if (list.length) setLevelId(String(list[0].id));
    });
  }, []);

  const savedForLevel = useMemo(
    () => saved.filter(r => String(r.departmentId) === String(levelId))
               .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0)),
    [saved, levelId]
  );
  const usingDefault = savedForLevel.length === 0;
  const levelDefault = useMemo(
    () => defaultPeriodsFor(levelName(levels.find(l => String(l.id) === String(levelId)))),
    [levels, levelId]
  );

  // Load the picked Level's list into the editor (default when unsaved).
  useEffect(() => {
    const base = savedForLevel.length ? savedForLevel : levelDefault;
    setRows(base.map(r => ({ code: r.code, label: r.label, semesterNo: r.semesterNo ?? null })));
    setMsg(""); setErr("");
  }, [savedForLevel, levelDefault]);

  const dirty = useMemo(() => {
    const base = (savedForLevel.length ? savedForLevel : levelDefault)
      .map(r => `${r.code}|${r.label}|${r.semesterNo ?? ""}`).join(";");
    return base !== rows.map(r => `${r.code}|${r.label}|${r.semesterNo ?? ""}`).join(";");
  }, [rows, savedForLevel, levelDefault]);

  function update(i, patch) { setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r))); }
  function move(i, dir) {
    setRows(rs => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = rs.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  function remove(i) { setRows(rs => rs.filter((_, idx) => idx !== i)); }
  function add() {
    setRows(rs => [...rs, { code: nextCode(rs), label: "", semesterNo: rs[rs.length - 1]?.semesterNo ?? null }]);
  }
  function applyPreset(key) {
    setRows(PRESETS[key].periods.map(p => ({ ...p })));
    setMsg(""); setErr("");
  }

  async function put(list, okText) {
    setBusy(true); setMsg(""); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/grading-periods/department/${levelId}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(list),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setSaved(await fetchGradingPeriods());
      setMsg(okText);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  function save() {
    if (rows.length === 0) { setErr("Add at least one grading period."); return; }
    if (rows.some(r => !r.label.trim())) { setErr("Every period needs a name."); return; }
    put(rows.map(r => ({ code: r.code, label: r.label.trim(), semesterNo: r.semesterNo })), "Grading periods saved.");
  }
  function resetToDefault() {
    if (!window.confirm("Reset this level to its default grading periods?")) return;
    put([], "Reset to the default grading periods.");
  }

  const level = levels.find(l => String(l.id) === String(levelId));

  return (
    <section className={`${s.card} ${s.maintCard}`}>
      <div className={s.cardHead}>
        <h2 className={s.cardTitle}>Grading Periods</h2>
        <p className={s.cardHint}>
          The grading columns teachers encode and students see, set per level. Pre-School,
          Elementary and Junior High use 1st–4th Quarter on every class (the subject runs all
          year); Senior High uses 2 quarters per semester; a College level would use
          Midterm / Final. Grades already encoded under a period you later remove stay visible.
        </p>
      </div>

      {levels.length === 0 ? (
        <div className={g.empty}>No teaching levels yet. Add them under Organization → Departments.</div>
      ) : (
        <>
          <div className={g.levelTabs} role="tablist" aria-label="Level">
            {levels.map(l => {
              const configured = saved.some(r => String(r.departmentId) === String(l.id));
              const active = String(l.id) === String(levelId);
              return (
                <button key={l.id} type="button" role="tab" aria-selected={active}
                        className={`${g.levelTab} ${active ? g.levelTabActive : ""}`}
                        onClick={() => setLevelId(String(l.id))}>
                  {levelName(l)}
                  {!configured && <span className={g.defaultTag}>default</span>}
                </button>
              );
            })}
          </div>

          <div className={g.presetRow}>
            <span className={g.presetLabel}>Quick fill:</span>
            {Object.entries(PRESETS).map(([key, p]) => (
              <button key={key} type="button" className={g.presetBtn} onClick={() => applyPreset(key)}>
                {p.label}
              </button>
            ))}
          </div>

          <div className={g.list}>
            <div className={g.listHead}>
              <span>#</span><span>Period name</span><span>Falls in</span><span />
            </div>
            {rows.map((r, i) => (
              <div key={r.code} className={g.row}>
                <span className={g.num}>{i + 1}</span>
                <input
                  className={g.input}
                  value={r.label}
                  maxLength={60}
                  placeholder="e.g. 1st Quarter"
                  aria-label={`Period ${i + 1} name`}
                  onChange={e => update(i, { label: e.target.value })}
                />
                <select
                  className={g.input}
                  value={r.semesterNo == null ? "" : String(r.semesterNo)}
                  aria-label={`Period ${i + 1} semester`}
                  onChange={e => update(i, { semesterNo: e.target.value === "" ? null : Number(e.target.value) })}>
                  {SEM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className={g.rowActions}>
                  <button type="button" className={g.iconBtn} onClick={() => move(i, -1)}
                          disabled={i === 0} aria-label="Move up" title="Move up">↑</button>
                  <button type="button" className={g.iconBtn} onClick={() => move(i, 1)}
                          disabled={i === rows.length - 1} aria-label="Move down" title="Move down">↓</button>
                  <button type="button" className={s.deleteBtn} onClick={() => remove(i)}
                          disabled={rows.length === 1}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          <div className={g.footer}>
            <button type="button" className={g.addBtn} onClick={add} disabled={rows.length >= 8}>
              + Add period
            </button>
            <span className={g.spacer} />
            {!usingDefault && (
              <button type="button" className={g.ghostBtn} onClick={resetToDefault} disabled={busy}>
                Reset to default
              </button>
            )}
            <button type="button" className={s.primaryBtn} onClick={save} disabled={busy || (!dirty && !usingDefault)}>
              {busy ? "Saving…" : `Save for ${levelName(level) || "this level"}`}
            </button>
          </div>
          <div className={s.maintFoot}>
            {usingDefault && !msg && (
              <span className={g.note}>This level isn't saved yet — it uses the default shown above.</span>
            )}
            {msg && <span className={s.flash}>{msg}</span>}
            {err && <span className={s.errorText}>{err}</span>}
          </div>
        </>
      )}
    </section>
  );
}
